import ForgotPasswordView from '@v2/domains/auth/views/ForgotPassword.view'
import LoginView from '@v2/domains/auth/views/Login.view'
import ResetPasswordView from '@v2/domains/auth/views/ResetPassword.view'
import SelectOrganizationView from '@v2/domains/organization/views/SelectOrganization.view'
import { createElement } from 'react'
import { RouteObject } from 'react-router-dom'

import {
  BenestreamMenuConfig,
  DefaultMenuConfig,
  Platform,
  RouteConfig,
} from '../types/routing.types'
import { BENESTREAM_ROUTES } from './benestream.routes'
import { DEFAULT_ROUTES } from './default.routes'
import {
  CUSTOM_ROUTES_CONFIG,
  PUBLIC_ROUTES_CONFIG,
} from './route-configs.constants'

//TODO migrate reset password a previews view
const PUBLIC_ROUTES: Record<string, RouteConfig> = {
  LOGIN: {
    ...PUBLIC_ROUTES_CONFIG.LOGIN,
    element: createElement(LoginView),
  },
  FORGOT_PASSWORD: {
    ...PUBLIC_ROUTES_CONFIG.FORGOT_PASSWORD,
    element: createElement(ForgotPasswordView),
  },
  SELECT_ORGANIZATION: {
    ...PUBLIC_ROUTES_CONFIG.SELECT_ORGANIZATION,
    element: createElement(SelectOrganizationView),
  },
  RESET_PASSWORD: {
    ...PUBLIC_ROUTES_CONFIG.RESET_PASSWORD,
    element: createElement(ResetPasswordView),
  },
}

const CUSTOM_ROUTE: RouteConfig = {
  ...CUSTOM_ROUTES_CONFIG,
  element: createElement('div'),
}

const ALL_ROUTES: Record<string, RouteConfig> = {
  ...PUBLIC_ROUTES,
  ...DEFAULT_ROUTES,
  ...BENESTREAM_ROUTES,
  CUSTOM_PATH: CUSTOM_ROUTE,
}

const routesByPlatformCache: Record<
  Platform,
  Record<string, RouteConfig> | null
> = {
  default: null,
  benestream: null,
}

const menuItemsCache: {
  default: DefaultMenuConfig[] | null
  benestream: BenestreamMenuConfig[] | null
  benestreamSettings: BenestreamMenuConfig[] | null
  routeObjects: Record<Platform, RouteObject[] | null>
} = {
  default: null,
  benestream: null,
  benestreamSettings: null,
  routeObjects: {
    default: null,
    benestream: null,
  },
}

export const getRoutesByPlatform = (
  platform: Platform,
  forceRefresh: boolean = false,
): Record<string, RouteConfig> => {
  if (!forceRefresh && routesByPlatformCache[platform]) {
    return routesByPlatformCache[platform]!
  }

  const filteredRoutes: Record<string, RouteConfig> = {}

  Object.entries(PUBLIC_ROUTES).forEach(([key, route]) => {
    filteredRoutes[key] = route
  })

  filteredRoutes['CUSTOM_PATH'] = CUSTOM_ROUTE

  const platformSpecificRoutes =
    platform === 'default' ? DEFAULT_ROUTES : BENESTREAM_ROUTES

  Object.entries(platformSpecificRoutes).forEach(([key, route]) => {
    filteredRoutes[key] = route
  })

  Object.entries(ALL_ROUTES)
    .filter(
      ([key, route]) =>
        !filteredRoutes[key] &&
        (!route.menuConfig?.platforms ||
          route.menuConfig.platforms.length === 0),
    )
    .forEach(([key, route]) => {
      filteredRoutes[key] = route
    })

  routesByPlatformCache[platform] = filteredRoutes

  return filteredRoutes
}

export const getDefaultMenuItems = (
  forceRefresh: boolean = false,
): DefaultMenuConfig[] => {
  if (!forceRefresh && menuItemsCache.default) {
    return menuItemsCache.default
  }

  const platformRoutes = getRoutesByPlatform('default')

  const menuItems = Object.values(platformRoutes)
    .filter((route) => route.showInMenu && route.menuConfig)
    .sort((a, b) => (a.menuConfig?.order || 0) - (b.menuConfig?.order || 0))
    .map((route) => ({
      id: route.key,
      path: route.path,
      title: route.label,
      icon: route.menuConfig?.icon,
      policy: route.policy,
    }))

  menuItemsCache.default = menuItems
  return menuItems
}

export const getBenestreamMenuItems = (
  forceRefresh: boolean = false,
): BenestreamMenuConfig[] => {
  if (!forceRefresh && menuItemsCache.benestream) {
    return menuItemsCache.benestream
  }

  const platformRoutes = getRoutesByPlatform('benestream')

  const menuItems = Object.values(platformRoutes)
    .filter(
      (route) =>
        route.showInMenu && route.menuConfig && route.key !== 'SETTINGS',
    )
    .sort((a, b) => (a.menuConfig?.order || 0) - (b.menuConfig?.order || 0))
    .map((route) => ({
      id: route.key,
      route: {
        path: route.path,
        policy: route.policy,
      },
      path: route.path,
      title: route.label,
      icon: route.menuConfig?.icon,
      policy: route.policy,
    }))

  menuItemsCache.benestream = menuItems as BenestreamMenuConfig[]
  return menuItems as BenestreamMenuConfig[]
}

export const getBenestreamSettingsMenuItems = (
  forceRefresh: boolean = false,
): BenestreamMenuConfig[] => {
  if (!forceRefresh && menuItemsCache.benestreamSettings) {
    return menuItemsCache.benestreamSettings
  }

  const settingsRoute = BENESTREAM_ROUTES.SETTINGS

  if (!settingsRoute) return []

  const menuItems = [
    {
      id: 'config',
      route: {
        path: settingsRoute.path,
        policy: settingsRoute.policy,
      },
      path: settingsRoute.path,
      title: settingsRoute.label,
      icon: settingsRoute.menuConfig?.icon,
    },
  ]

  menuItemsCache.benestreamSettings = menuItems as BenestreamMenuConfig[]
  return menuItems as BenestreamMenuConfig[]
}

export const getRouteObjects = (
  platform: Platform,
  forceRefresh: boolean = false,
): RouteObject[] => {
  if (!forceRefresh && menuItemsCache.routeObjects[platform]) {
    return menuItemsCache.routeObjects[platform]!
  }

  const platformRoutes = getRoutesByPlatform(platform)

  const routeObjects = Object.values(platformRoutes)
    .filter((route) => route.key !== 'CUSTOM_PATH')
    .map((route) => ({
      path: route.path.startsWith('/') ? route.path.substring(1) : route.path,
      element: route.element,
    }))

  menuItemsCache.routeObjects[platform] = routeObjects
  return routeObjects
}
